import { useCallback, useEffect, useRef, useState } from 'react'
import type { LoginResponse } from '../api/auth'
import { ApiError } from '../api/client'
import {
  CREDENTIAL_KINDS,
  credentialVaultApi,
  type Credential,
  type CredentialBody,
  type CredentialKind,
  type CredentialList,
  type ReceivedCode,
} from '../api/credentialVault'
import {
  AccessDenied,
  Button,
  Callout,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  DataTable,
  EmptyState,
  FormGrid,
  IconButton,
  LoadingState,
  Page,
  PageHeader,
  Select,
  StatusPill,
  TextField,
  useConfirm,
  usePrompt,
  useSnackbar,
} from '../components/ui'

/**
 * R5-10 — cassaforte credenziali di portali e caselle mail. Solo Admin, attiva con il flag «credenziali.cassaforte».
 * I segreti (password, segreto TOTP, password IMAP) sono cifrati sul server e non tornano mai negli elenchi: la password
 * si mostra solo con un motivo, il codice TOTP e i codici ricevuti per mail con chiamate dedicate. Tutto finisce in audit.
 */

interface CredentialForm {
  name: string
  kind: CredentialKind
  url: string
  username: string
  password: string
  clearPassword: boolean
  notes: string
  totp: string
  clearTotp: boolean
  imapEnabled: boolean
  imapHost: string
  imapPort: string
  imapUseSsl: boolean
  imapUsername: string
  imapPassword: string
  clearImapPassword: boolean
  imapFolder: string
  imapSenderFilter: string
  imapCodeRegex: string
}

const EMPTY_FORM: CredentialForm = {
  name: '', kind: 'portale', url: '', username: '', password: '', clearPassword: false, notes: '',
  totp: '', clearTotp: false, imapEnabled: false, imapHost: '', imapPort: '993', imapUseSsl: true, imapUsername: '',
  imapPassword: '', clearImapPassword: false, imapFolder: 'INBOX', imapSenderFilter: '', imapCodeRegex: '',
}

const REVEAL_SECONDS = 30
const MAX_QR_BYTES = 5 * 1024 * 1024

function errMsg(e: unknown, fallback: string): string {
  return e instanceof Error ? e.message : fallback
}

function dateTime(iso: string | null | undefined): string {
  return iso ? new Date(iso).toLocaleString('it-IT', { dateStyle: 'short', timeStyle: 'short' }) : '—'
}

function time(iso: string): string {
  return new Date(iso).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })
}

const kindLabel = (k: string) => CREDENTIAL_KINDS.find(o => o.value === k)?.label ?? k

/** Segreto: testo nuovo = nuovo valore, «cancella» = '', altrimenti null (invariato). */
function secret(value: string, clear: boolean): string | null {
  if (value !== '') return value
  return clear ? '' : null
}

function readAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result ?? ''))
    reader.onerror = () => reject(new Error('Impossibile leggere il file.'))
    reader.readAsDataURL(file)
  })
}

async function copy(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text)
    return true
  } catch {
    return false
  }
}

export function CredentialVaultPage({ auth }: { auth: LoginResponse }) {
  const isAdmin = auth.role === 'Administrator' || auth.role === 'EqualAdmin'
  const confirm = useConfirm()
  const prompt = usePrompt()
  const snackbar = useSnackbar()
  const [data, setData] = useState<CredentialList | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [disabled, setDisabled] = useState(false)
  const [form, setForm] = useState<CredentialForm | null>(null)
  const [editing, setEditing] = useState<Credential | null>(null)
  const [saving, setSaving] = useState(false)
  const [formError, setFormError] = useState('')
  const [openId, setOpenId] = useState<number | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      setData(await credentialVaultApi.list())
      setDisabled(false)
    } catch (e) {
      if (e instanceof ApiError && e.status === 404) setDisabled(true)
      else setError(errMsg(e, 'Impossibile caricare le credenziali.'))
    } finally {
      setLoading(false)
    }
  }, [])
  useEffect(() => { if (isAdmin) void load() }, [isAdmin, load])

  if (!isAdmin) return <Page><AccessDenied /></Page>

  function openNew() {
    setEditing(null)
    setForm({ ...EMPTY_FORM })
    setFormError('')
  }

  function openEdit(c: Credential) {
    setEditing(c)
    setForm({
      name: c.name,
      kind: c.kind,
      url: c.url ?? '',
      username: c.username ?? '',
      password: '',
      clearPassword: false,
      notes: c.notes ?? '',
      totp: '',
      clearTotp: false,
      imapEnabled: c.imap.enabled,
      imapHost: c.imap.host ?? '',
      imapPort: String(c.imap.port),
      imapUseSsl: c.imap.useSsl,
      imapUsername: c.imap.username ?? '',
      imapPassword: '',
      clearImapPassword: false,
      imapFolder: c.imap.folder,
      imapSenderFilter: c.imap.senderFilter ?? '',
      imapCodeRegex: c.imap.codeRegex ?? '',
    })
    setFormError('')
  }

  async function save() {
    if (!form) return
    if (!form.name.trim()) { setFormError('Il nome è obbligatorio.'); return }
    const port = Number(form.imapPort)
    if (!Number.isInteger(port) || port < 1 || port > 65535) { setFormError('Porta IMAP non valida.'); return }
    const body: CredentialBody = {
      name: form.name.trim(),
      kind: form.kind,
      url: form.url.trim() || null,
      username: form.username.trim() || null,
      password: secret(form.password, form.clearPassword),
      notes: form.notes.trim() || null,
      totp: secret(form.totp.trim(), form.clearTotp),
      imap: {
        enabled: form.imapEnabled,
        host: form.imapHost.trim() || null,
        port,
        useSsl: form.imapUseSsl,
        username: form.imapUsername.trim() || null,
        password: secret(form.imapPassword, form.clearImapPassword),
        folder: form.imapFolder.trim() || 'INBOX',
        senderFilter: form.imapSenderFilter.trim() || null,
        codeRegex: form.imapCodeRegex.trim() || null,
      },
    }
    setSaving(true)
    setFormError('')
    try {
      if (editing === null) await credentialVaultApi.create(body)
      else await credentialVaultApi.update(editing.id, body)
      setForm(null)
      setEditing(null)
      void load()
    } catch (e) {
      setFormError(errMsg(e, 'Errore durante il salvataggio.'))
    } finally {
      setSaving(false)
    }
  }

  async function remove(c: Credential) {
    if (!(await confirm({
      title: 'Elimina credenziale',
      message: `Eliminare «${c.name}» con i suoi segreti e i codici ricevuti? L’operazione non si annulla.`,
      confirmLabel: 'Elimina',
      destructive: true,
    }))) return
    try {
      await credentialVaultApi.remove(c.id)
      if (openId === c.id) setOpenId(null)
      void load()
    } catch (e) {
      setError(errMsg(e, 'Errore durante l’eliminazione.'))
    }
  }

  const items = data?.items ?? []
  const opened = items.find(c => c.id === openId) ?? null

  return (
    <Page>
      <PageHeader title="Credenziali" icon="key" />
      <div className="d-flex flex-column gap-3">
        <Callout tone="info" role="note">
          Password, segreti 2FA e password delle caselle sono cifrati e non compaiono mai negli elenchi.
          Mostrare una password chiede il motivo; ogni lettura, modifica e uso di un codice resta nell’audit.
          I codici di verifica ricevuti per mail durano 15 minuti e il testo delle mail non viene salvato.
        </Callout>
        {disabled && (
          <Callout tone="warning">
            La cassaforte credenziali non è attiva: accendi il flag «credenziali.cassaforte» nelle impostazioni.
          </Callout>
        )}
        {data && !data.encryptionAvailable && (
          <Callout tone="error">
            Chiave di cifratura non configurata: non è possibile salvare né leggere segreti finché non viene impostata.
          </Callout>
        )}
        {error && <Callout tone="error">{error}</Callout>}
        {!disabled && !form && (
          <div className="d-flex gap-2">
            <Button variant="filled" icon="add" onClick={openNew}>Nuova credenziale</Button>
          </div>
        )}

        {form && (
          <Card>
            <CardHeader title={editing === null ? 'Nuova credenziale' : `Modifica «${editing.name}»`} />
            <CardContent>
              <FormGrid columns={3}>
                <div>
                  <TextField label="Nome" required maxLength={200} value={form.name}
                    onChange={e => setForm({ ...form, name: e.target.value })} />
                </div>
                <div>
                  <Select label="Tipo" value={form.kind}
                    onChange={e => setForm({ ...form, kind: e.target.value as CredentialKind })}>
                    {CREDENTIAL_KINDS.map(k => <option key={k.value} value={k.value}>{k.label}</option>)}
                  </Select>
                </div>
                <div>
                  <TextField label="Indirizzo (URL)" type="url" maxLength={1000} placeholder="https://" value={form.url}
                    onChange={e => setForm({ ...form, url: e.target.value })} />
                </div>
                <div>
                  <TextField label="Utente" maxLength={320} autoComplete="off" value={form.username}
                    onChange={e => setForm({ ...form, username: e.target.value })} />
                </div>
                <div>
                  <TextField label="Password" type="password" autoComplete="new-password" value={form.password}
                    placeholder={editing?.hasPassword ? 'invariata' : ''}
                    supportingText={editing?.hasPassword ? 'Lascia vuoto per non cambiarla.' : undefined}
                    onChange={e => setForm({ ...form, password: e.target.value, clearPassword: false })} />
                </div>
                <div className="d-flex align-items-end">
                  {editing?.hasPassword && (
                    <Checkbox id="vault-clear-password" label="Cancella la password salvata" checked={form.clearPassword}
                      onChange={e => setForm({ ...form, clearPassword: e.target.checked, password: '' })} />
                  )}
                </div>
                <div style={{ gridColumn: '1 / -1' }}>
                  <TextField label="Note" multiline rows={2} maxLength={4000} value={form.notes}
                    supportingText="Le note non sono cifrate: non scriverci segreti."
                    onChange={e => setForm({ ...form, notes: e.target.value })} />
                </div>
                <div style={{ gridColumn: 'span 2' }}>
                  <TextField label="2FA (TOTP)" autoComplete="off" value={form.totp}
                    placeholder={editing?.hasTotp ? 'già configurato' : 'otpauth://totp/… oppure il segreto'}
                    supportingText="Incolla l’URI otpauth:// o il segreto Base32. Il QR si carica dal dettaglio dopo il salvataggio."
                    onChange={e => setForm({ ...form, totp: e.target.value, clearTotp: false })} />
                </div>
                <div className="d-flex align-items-end">
                  {editing?.hasTotp && (
                    <Checkbox id="vault-clear-totp" label="Rimuovi il 2FA" checked={form.clearTotp}
                      onChange={e => setForm({ ...form, clearTotp: e.target.checked, totp: '' })} />
                  )}
                </div>
              </FormGrid>

              <h3 className="md-typescale-title-small mt-4 mb-2">Ascolto codici per mail (IMAP)</h3>
              <FormGrid columns={3}>
                <div style={{ gridColumn: '1 / -1' }}>
                  <Checkbox id="vault-imap-enabled" label="Leggi i codici di verifica da questa casella" checked={form.imapEnabled}
                    onChange={e => setForm({ ...form, imapEnabled: e.target.checked })} />
                </div>
                <div>
                  <TextField label="Server IMAP" maxLength={255} placeholder="imap.esempio.it" value={form.imapHost}
                    onChange={e => setForm({ ...form, imapHost: e.target.value })} />
                </div>
                <div>
                  <TextField label="Porta" inputMode="numeric" value={form.imapPort}
                    onChange={e => setForm({ ...form, imapPort: e.target.value.replace(/\D/g, '') })} />
                </div>
                <div className="d-flex align-items-end">
                  <Checkbox id="vault-imap-ssl" label="SSL/TLS" checked={form.imapUseSsl}
                    onChange={e => setForm({ ...form, imapUseSsl: e.target.checked })} />
                </div>
                <div>
                  <TextField label="Utente IMAP" maxLength={320} autoComplete="off" value={form.imapUsername}
                    onChange={e => setForm({ ...form, imapUsername: e.target.value })} />
                </div>
                <div>
                  <TextField label="Password IMAP" type="password" autoComplete="new-password" value={form.imapPassword}
                    placeholder={editing?.imap.hasPassword ? 'invariata' : ''}
                    onChange={e => setForm({ ...form, imapPassword: e.target.value, clearImapPassword: false })} />
                </div>
                <div className="d-flex align-items-end">
                  {editing?.imap.hasPassword && (
                    <Checkbox id="vault-clear-imap-password" label="Cancella la password IMAP" checked={form.clearImapPassword}
                      onChange={e => setForm({ ...form, clearImapPassword: e.target.checked, imapPassword: '' })} />
                  )}
                </div>
                <div>
                  <TextField label="Cartella" maxLength={200} value={form.imapFolder}
                    onChange={e => setForm({ ...form, imapFolder: e.target.value })} />
                </div>
                <div>
                  <TextField label="Mittenti ammessi" maxLength={500} placeholder="tutti" value={form.imapSenderFilter}
                    supportingText="Indirizzi o domini separati da virgola."
                    onChange={e => setForm({ ...form, imapSenderFilter: e.target.value })} />
                </div>
                <div>
                  <TextField label="Regex del codice" maxLength={500} placeholder="predefinita" value={form.imapCodeRegex}
                    supportingText="Vuota: 4–8 cifre vicino a «codice», «code», «OTP». Il gruppo «code» o il primo gruppo è il codice."
                    onChange={e => setForm({ ...form, imapCodeRegex: e.target.value })} />
                </div>
              </FormGrid>
              {formError && <div className="md-typescale-body-small text-danger mt-2">{formError}</div>}
              <div className="mt-3 d-flex gap-2">
                <Button variant="text" disabled={saving} onClick={() => { setForm(null); setEditing(null) }}>Annulla</Button>
                <Button variant="tonal" disabled={saving} onClick={() => void save()}>Salva</Button>
              </div>
            </CardContent>
          </Card>
        )}

        {loading ? (
          <LoadingState />
        ) : disabled ? null : items.length === 0 ? (
          <EmptyState icon="key" title="Nessuna credenziale salvata." />
        ) : (
          <DataTable>
            <thead>
              <tr>
                <th>Nome</th><th>Tipo</th><th>Indirizzo</th><th>Utente</th><th>Password</th><th>2FA</th>
                <th>Codici via mail</th><th />
              </tr>
            </thead>
            <tbody>
              {items.map(c => (
                <tr key={c.id} className={c.id === openId ? 'table-active' : ''}>
                  <td>{c.name}</td>
                  <td>{kindLabel(c.kind)}</td>
                  <td className="text-truncate" style={{ maxWidth: 240 }}>
                    {c.url ? <a href={c.url} target="_blank" rel="noopener noreferrer">{c.url}</a> : '—'}
                  </td>
                  <td>{c.username ?? '—'}</td>
                  <td>{c.hasPassword ? <StatusPill tone="success">Salvata</StatusPill> : <StatusPill tone="neutral">Nessuna</StatusPill>}</td>
                  <td>{c.hasTotp ? <StatusPill tone="success">TOTP</StatusPill> : '—'}</td>
                  <td>
                    {!c.imap.enabled ? '—'
                      : c.imap.lastError ? <StatusPill tone="error">Errore</StatusPill>
                      : <StatusPill tone="info">{c.activeCodes > 0 ? `${c.activeCodes} attivi` : 'In ascolto'}</StatusPill>}
                  </td>
                  <td className="text-end text-nowrap">
                    <IconButton icon={c.id === openId ? 'expand_less' : 'expand_more'} size="sm"
                      aria-label={c.id === openId ? 'Chiudi dettaglio' : 'Apri dettaglio'}
                      onClick={() => setOpenId(c.id === openId ? null : c.id)} />
                    <IconButton icon="edit" size="sm" aria-label="Modifica" onClick={() => openEdit(c)} />
                    <IconButton icon="delete" size="sm" aria-label="Elimina" onClick={() => void remove(c)} />
                  </td>
                </tr>
              ))}
            </tbody>
          </DataTable>
        )}

        {opened && (
          <CredentialDetail
            key={opened.id}
            credential={opened}
            onChanged={() => void load()}
            askReason={() => prompt({
              title: 'Mostra password',
              message: `La password di «${opened.name}» verrà mostrata per ${REVEAL_SECONDS} secondi e la lettura resta nell’audit.`,
              label: 'Motivo',
              required: true,
              confirmLabel: 'Mostra',
            })}
            notify={message => snackbar.show({ message })}
          />
        )}
      </div>
    </Page>
  )
}

function CredentialDetail({ credential: c, onChanged, askReason, notify }: {
  credential: Credential
  onChanged: () => void
  askReason: () => Promise<string | null>
  notify: (message: string) => void
}) {
  const [error, setError] = useState('')
  const [password, setPassword] = useState<string | null>(null)
  const [passwordLeft, setPasswordLeft] = useState(0)
  const [totp, setTotp] = useState<{ code: string; expiresAt: number; period: number } | null>(null)
  const [now, setNow] = useState(() => Date.now())
  const [codes, setCodes] = useState<ReceivedCode[] | null>(null)
  const [busy, setBusy] = useState(false)
  const fileRef = useRef<HTMLInputElement | null>(null)

  // Un solo orologio per il conto alla rovescia di password e TOTP.
  useEffect(() => {
    if (password === null && totp === null) return
    const t = window.setInterval(() => setNow(Date.now()), 1000)
    return () => window.clearInterval(t)
  }, [password, totp])

  useEffect(() => {
    if (password === null) return
    if (passwordLeft <= 0) { setPassword(null); return }
    const t = window.setTimeout(() => setPasswordLeft(s => s - 1), 1000)
    return () => window.clearTimeout(t)
  }, [password, passwordLeft])

  const totpLeft = totp ? Math.max(0, Math.ceil((totp.expiresAt - now) / 1000)) : 0
  useEffect(() => {
    if (totp && totpLeft === 0) setTotp(null)
  }, [totp, totpLeft])

  const loadCodes = useCallback(async () => {
    if (!c.imap.enabled && c.activeCodes === 0) { setCodes([]); return }
    try {
      setCodes(await credentialVaultApi.codes(c.id))
    } catch (e) {
      setError(errMsg(e, 'Impossibile leggere i codici ricevuti.'))
    }
  }, [c.id, c.imap.enabled, c.activeCodes])
  useEffect(() => { void loadCodes() }, [loadCodes])

  async function reveal() {
    const reason = await askReason()
    if (reason === null) return
    setError('')
    try {
      const r = await credentialVaultApi.revealPassword(c.id, reason.trim())
      setPassword(r.password)
      setPasswordLeft(REVEAL_SECONDS)
    } catch (e) {
      setError(errMsg(e, 'Impossibile mostrare la password.'))
    }
  }

  async function totpCode() {
    setError('')
    try {
      const r = await credentialVaultApi.totpCode(c.id)
      setNow(Date.now())
      setTotp({ code: r.code, expiresAt: Date.now() + r.remainingSeconds * 1000, period: r.period })
    } catch (e) {
      setError(errMsg(e, 'Impossibile generare il codice.'))
    }
  }

  async function uploadQr(file: File | undefined) {
    if (!file) return
    if (file.size > MAX_QR_BYTES) { setError('Immagine troppo grande (massimo 5 MB).'); return }
    setBusy(true)
    setError('')
    try {
      await credentialVaultApi.uploadTotpQr(c.id, await readAsDataUrl(file))
      notify('2FA configurato dal QR.')
      onChanged()
    } catch (e) {
      setError(errMsg(e, 'QR non leggibile.'))
    } finally {
      setBusy(false)
      if (fileRef.current) fileRef.current.value = ''
    }
  }

  async function poll() {
    setBusy(true)
    setError('')
    try {
      const r = await credentialVaultApi.poll(c.id)
      if (r.lastError) setError(r.lastError)
      else notify(r.captured > 0 ? `Codici nuovi: ${r.captured}.` : 'Nessun codice nuovo.')
      await loadCodes()
      onChanged()
    } catch (e) {
      setError(errMsg(e, 'Controllo della casella non riuscito.'))
    } finally {
      setBusy(false)
    }
  }

  async function copyValue(value: string) {
    notify((await copy(value)) ? 'Copiato negli appunti.' : 'Copia non riuscita.')
  }

  return (
    <Card>
      <CardHeader title={c.name} subtitle={kindLabel(c.kind)} />
      <CardContent>
        {error && <Callout tone="error" className="mb-3">{error}</Callout>}
        <FormGrid columns={3}>
          <div>
            <div className="md-typescale-label-medium text-muted mb-1">Password</div>
            {!c.hasPassword ? <span>Nessuna password salvata.</span>
              : password !== null ? (
                <div className="d-flex align-items-center gap-2">
                  <code className="md-typescale-body-large">{password}</code>
                  <IconButton icon="content_copy" size="sm" aria-label="Copia password" onClick={() => void copyValue(password)} />
                  <IconButton icon="visibility_off" size="sm" aria-label="Nascondi" onClick={() => setPassword(null)} />
                  <span className="md-typescale-body-small text-muted">{passwordLeft} s</span>
                </div>
              ) : (
                <Button variant="tonal" icon="visibility" onClick={() => void reveal()}>Mostra…</Button>
              )}
          </div>
          <div>
            <div className="md-typescale-label-medium text-muted mb-1">
              2FA {c.hasTotp ? `(${c.totpDigits} cifre, ${c.totpPeriod} s, ${c.totpAlgorithm})` : ''}
            </div>
            {c.hasTotp && (totp ? (
              <div className="d-flex align-items-center gap-2">
                <code className="md-typescale-headline-small">{totp.code}</code>
                <IconButton icon="content_copy" size="sm" aria-label="Copia codice" onClick={() => void copyValue(totp.code)} />
                <span className="md-typescale-body-small text-muted">scade tra {totpLeft} s</span>
              </div>
            ) : (
              <Button variant="tonal" icon="pin" onClick={() => void totpCode()}>Genera codice</Button>
            ))}
            <div className="mt-2">
              <input ref={fileRef} type="file" accept="image/*" hidden
                onChange={e => void uploadQr(e.target.files?.[0])} />
              <Button variant="text" icon="qr_code_scanner" disabled={busy} onClick={() => fileRef.current?.click()}>
                {c.hasTotp ? 'Sostituisci dal QR…' : 'Configura dal QR…'}
              </Button>
            </div>
            {c.totpLabel && <div className="md-typescale-body-small text-muted">{c.totpLabel}</div>}
          </div>
          <div>
            <div className="md-typescale-label-medium text-muted mb-1">Casella IMAP</div>
            {!c.imap.enabled ? <span>Ascolto non attivo.</span> : (
              <>
                <div className="md-typescale-body-medium">{c.imap.host}:{c.imap.port} · {c.imap.folder}</div>
                <div className="md-typescale-body-small text-muted">Ultimo controllo: {dateTime(c.imap.lastPolledAt)}</div>
                {c.imap.lastError && <div className="md-typescale-body-small text-danger">{c.imap.lastError}</div>}
                <Button variant="text" icon="sync" className="mt-1" disabled={busy} onClick={() => void poll()}>Controlla ora</Button>
              </>
            )}
          </div>
        </FormGrid>
        {c.notes && (
          <div className="mt-3">
            <div className="md-typescale-label-medium text-muted mb-1">Note</div>
            <div className="md-typescale-body-medium" style={{ whiteSpace: 'pre-wrap' }}>{c.notes}</div>
          </div>
        )}

        {(c.imap.enabled || (codes?.length ?? 0) > 0) && (
          <div className="mt-4">
            <div className="d-flex align-items-center gap-2 mb-2">
              <h3 className="md-typescale-title-small m-0">Codici ricevuti (ultimi 15 minuti)</h3>
              <IconButton icon="refresh" size="sm" aria-label="Aggiorna codici" onClick={() => void loadCodes()} />
            </div>
            {codes === null ? <LoadingState /> : codes.length === 0 ? (
              <EmptyState icon="mark_email_read" title="Nessun codice valido al momento." />
            ) : (
              <DataTable>
                <thead><tr><th>Codice</th><th>Mittente</th><th>Ricevuto</th><th>Scade</th><th /></tr></thead>
                <tbody>
                  {codes.map(x => (
                    <tr key={x.id}>
                      <td><code className="md-typescale-title-medium">{x.code}</code></td>
                      <td>{x.senderDomain ?? '—'}</td>
                      <td>{time(x.receivedAt)}</td>
                      <td>{time(x.expiresAt)}</td>
                      <td className="text-end">
                        <IconButton icon="content_copy" size="sm" aria-label="Copia codice" onClick={() => void copyValue(x.code)} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </DataTable>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  )
}
